<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку моделей'"
                    :to="router.resolve({ name: 'HandbookModels' }).href"
                />
            </div>

            <div class="content-header__title">Добавление модели</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <div class="content__body">
            <div class="form__content">
                <div class="form__fields">
                    <UIBrandImageableRadio
                        v-model="formBrand"
                        :error-message="brandErrors.join() ?? ''"
                    />

                    <UIInput
                        id="name"
                        v-model="form.name"
                        label="Название модели*"
                        :error-message="errors?.name?.join() ?? ''"
                    />

                    <UIInput
                        id="dealerName"
                        v-model="form.symbolic_code"
                        label="Символьный код модели*"
                        :error-message="errors?.symbolic_code?.join() ?? ''"
                    />
                </div>

                <div class="form__fields">
                    <div class="form__subtitle">Коды модели</div>

                    <DataTable
                        :headers="headers"
                        :data="form.model_codes"
                        has-actions
                    >
                        <template #model_code="{ row, rowIndex }">
                            <div>{{ row.model_code }}</div>

                            <span class="form-field__error">
                                {{ getNewModelCodeErrors(rowIndex).model_code?.join() }}
                            </span>
                        </template>

                        <template #production_start_year="{ row, rowIndex }">
                            <div>{{ row.production_start_year }}</div>

                            <span class="form-field__error">
                                {{ getNewModelCodeErrors(rowIndex).production_start_year?.join() }}
                            </span>
                        </template>

                        <template #production_end_year="{ row, rowIndex }">
                            <div>{{ row.production_end_year }}</div>

                            <span class="form-field__error">
                                {{ getNewModelCodeErrors(rowIndex).production_end_year?.join() }}
                            </span>
                        </template>

                        <template #actions="{ row }">
                            <UIDropDown as-modal-on-mobile>
                                <template #elements>
                                    <!--<li
                                        class="ui-dropdown-menu__element"
                                        @click="handleEditModelCode(row as ModelCodeCreateForm)"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                                        <span>Редактировать</span>
                                    </li>-->

                                    <li
                                        class="ui-dropdown-menu__element danger"
                                        @click="handleDeleteModelCode(row as ModelCodeCreateForm)"
                                    >
                                        <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                                        <span>Удалить</span>
                                    </li>
                                </template>
                            </UIDropDown>
                        </template>
                    </DataTable>

                    <AddModelCodeAction
                        @add-model-code="onAddModelCode"
                    />
                </div>
            </div>
        </div>

        <div class="content__footer">
            <div class="form__actions">
                <UISimpleButton
                    button-type="outlined"
                    @clicked="goToModelsPage"
                >
                    Выйти без сохранения
                </UISimpleButton>

                <UISimpleButton
                    :loading="saveLoading"
                    @clicked="saveModel"
                >
                    Сохранить
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, watch } from 'vue';
    import { useRouter } from 'vue-router';

    import {
        ModelCreateForm,
        ModelCreateFormErrors,
    } from '@/types/model.type.ts';
    import {
        ModelCodeCreateForm,
        ModelCodeCreateFormErrors,
        ModelCodeUpdateFormErrors,
    } from '@/types/model-code.type.ts';

    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIBrandImageableRadio from '@/components/ui/UIBrandImageableRadio/UIBrandImageableRadio.vue';
    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import AddModelCodeAction from '@/components/models/AddModelCodeAction/AddModelCodeAction.vue';

    const router = useRouter();
    const { createModel } = useModelRepository();
    const { showSuccess } = useNotifications();
    const { setDirty } = useUnsavedDataWarning();

    const form = ref<ModelCreateForm>({
        brand_id: 1,
        name: '',
        symbolic_code: '',
        model_codes: [],
    });
    const saveLoading = ref<boolean>(false);
    const errors = ref<ModelCreateFormErrors | null>(null);
    const headers = ref([
        { label: 'Код модели', key: 'model_code', sortable: false },
        { label: 'Год начала производства', key: 'production_start_year', sortable: false },
        { label: 'Год окончания производства', key: 'production_end_year', sortable: false },
    ]);

    const formBrand = computed<string>({
        get() {
            if (form.value.brand_id === 1) {
                return 'hyundai';
            }
            else if (form.value.brand_id === 2) {
                return 'genesis';
            }

            return '';
        },
        set(brandValue: string) {
            if (brandValue === 'hyundai') {
                form.value.brand_id = 1;
            }
            else if (brandValue === 'genesis') {
                form.value.brand_id = 2;
            }
        },
    });

    const brandErrors = computed((): string[] => {
        return (errors.value?.brand_id ?? [])
            .concat(errors.value?.brand_id ?? []);
    });

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    function saveModel(): void {
        saveLoading.value = true;

        createModel(form.value)
            .then(() => {
                setDirty(false);

                showSuccess({
                    title: 'Модель добавлена',
                });

                goToModelsPage();
            })
            .catch((createErrors: ModelCreateFormErrors) => {
                errors.value = createErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    // function handleEditModelCode(modelCode: ModelCodeCreateForm) {
    //     console.log('Редактировать код модели: ', modelCode);
    // }

    function handleDeleteModelCode(modelCode: ModelCodeCreateForm): void {
        form.value.model_codes.splice(form.value.model_codes.indexOf(modelCode), 1);

        if (errors.value?.model_codes) {
            errors.value.model_codes = undefined;
        }
    }

    function onAddModelCode(newModelCode: ModelCodeCreateForm): void {
        form.value.model_codes.push(newModelCode);
    }

    function getNewModelCodeErrors(index: number): ModelCodeCreateFormErrors {
        let modelCodeErrors: ModelCodeUpdateFormErrors = {};

        if (errors.value?.model_codes && index < errors.value.model_codes.length) {
            modelCodeErrors = {
                model_code: [...(errors.value.model_codes[index]?.model_code ?? [])],
                production_start_year: [...(errors.value.model_codes[index]?.production_start_year ?? [])],
                production_end_year: [...(errors.value.model_codes[index]?.production_end_year ?? [])],
            };
        }

        return modelCodeErrors;
    }

    function goToModelsPage(): void {
        router.push({ name: 'HandbookModels' });
    }
</script>
